import { UPDATE_GUIDE_PATH } from '../config/agentAccess';

/**
 * The ways an assistant reaches keepr, as the API names them on a key's
 * `lastClient.channel` (GET /api/api-keys), with the words a person reads
 * and the section of the update guide that covers each one.
 *
 * `anchor` is the heading's id in docs/guides/assistants/update-your-assistant.md
 * as the docs portal renders it (utils/markdown.js headingSlug); a test pins
 * the two together, so renaming a heading fails here rather than landing a
 * link on the top of the page.
 */
export const CLIENT_CHANNELS = {
	connector: { name: 'the keepr connector', anchor: 'the-connector' },
	plugin: { name: 'the keepr plugin for Claude Code', anchor: 'claude-code-plugin' },
	skill: { name: 'the keepr skill', anchor: 'another-coding-agent' },
	'claude-ai': { name: 'the keepr skill in Claude', anchor: 'the-skill-in-claude' },
	extension: { name: 'the keepr extension for Claude desktop', anchor: 'claude-desktop-extension' },
	local: { name: "keepr's MCP server", anchor: 'set-up-by-hand' },
};

// A channel this client does not know yet (a newer API) still reads as
// something, from the product alone.
const PRODUCT_NAMES = {
	'keepr-skill': 'the keepr skill',
	'keepr-mcp': "keepr's MCP server",
};

/** The update guide, at the section for `channel` — or at its top. */
export function clientUpdateHref(channel) {
	const known = CLIENT_CHANNELS[channel];
	return known ? `${UPDATE_GUIDE_PATH}#${known.anchor}` : UPDATE_GUIDE_PATH;
}

/**
 * A key's `lastClient`, read for the keys list:
 *   { label: 'Last used by the keepr plugin for Claude Code',
 *     outOfDate: false, updateHref: '/docs/guides/…#claude-code-plugin' }
 * or null when there is nothing to say — the key was never used by a keepr
 * client, or the API predates `lastClient` and did not send it at all.
 * `outOfDate` is true only on an explicit `upToDate: false`; an absent flag
 * is not a reason to alarm anyone.
 */
export function describeLastClient(lastClient) {
	if (!lastClient || typeof lastClient !== 'object') return null;
	const name = CLIENT_CHANNELS[lastClient.channel]?.name || PRODUCT_NAMES[lastClient.product];
	if (!name) return null;
	// No version number in the words: the plugin is numbered by its skill,
	// its server by its own, and "the plugin 0.2.4" is a number nobody has.
	// Out of date or not is what a person needs.
	return {
		label: `Last used by ${name}`,
		outOfDate: lastClient.upToDate === false,
		updateHref: clientUpdateHref(lastClient.channel),
	};
}
